import { DefaultTemplate } from "@components/templates/default";
import { HttpError, useGetIdentity, useGo, useNavigation, useShow, useTranslate } from "@refinedev/core";
import dayjs from "dayjs";
import { FaChevronLeft, FaPencil } from "react-icons/fa6";
import { Article } from "types/articles";
import { RichTextRenderer } from "@components/organisms/RichTextRenderer";
import { IAuthUser } from "@auth/firebase/interfaces";

export const ContentShow = () => {
    const translate = useTranslate();
    const { queryResult: { data, isLoading, isError } } = useShow<Article, HttpError>({
        resource: 'articles',
        meta: {
            fields: ['id', 'title', 'content', 'author_id', 'landing_image', 'tags', 'published_at', 'created_at', 'updated_at']
        }
    });
    const { goBack } = useNavigation();
    const go = useGo();
    const { data: user } = useGetIdentity<IAuthUser>();

    const post = data?.data;
    const isOwner = user?.user_id === post?.author_id;

    // Use default background or landing image
    const landingImage = post?.landing_image;

    if (isLoading) {
        return (
            <DefaultTemplate>
                <div className="flex items-center justify-center min-h-[60vh]">
                    <span className="loading loading-spinner loading-lg text-primary"></span>
                </div>
            </DefaultTemplate>
        );
    }

    if (isError || !post) {
        return (
            <DefaultTemplate>
                <div className="alert alert-error mx-4 my-8">
                    <span>{translate("articles.errors.not_found", "No se pudo encontrar el artículo.")}</span>
                </div>
            </DefaultTemplate>
        );
    }

    return (
        <DefaultTemplate
            left={
                <button className="btn btn-ghost gap-2" onClick={goBack}>
                    <FaChevronLeft />
                    <span>{translate("common.buttons.back", "Volver")}</span>
                </button>
            }
            right={
                isOwner ? <button className="btn btn-primary gap-2" onClick={() => go({ to: `/content/edit/${post.id}` })}>
                    <FaPencil />
                    <span>{translate("common.buttons.edit", "Editar")}</span>
                </button> : null
            }
        >
            <article className="max-w-4xl mx-auto my-8 px-4 prose prose-lg lg:prose-xl">
                {landingImage && (
                    <div className="w-full h-[40vh] mb-8 overflow-hidden rounded-box shadow-xl">
                        <img
                            src={landingImage}
                            alt={post.title}
                            className="w-full h-full object-cover"
                        />
                    </div>
                )}

                <header className="mb-12 not-prose">
                    <div className="flex flex-wrap gap-2 mb-4">
                        {post.tags?.map(tag => (
                            <span key={tag} className="badge badge-primary px-3 py-1 uppercase text-xs font-bold">
                                {tag}
                            </span>
                        ))}
                    </div>

                    <h1 className="text-4xl md:text-5xl font-black mb-4 leading-tight text-base-content">
                        {post.title}
                    </h1>

                    <div className="flex items-center gap-4 text-sm text-base-content/60 italic">
                        <span>{dayjs(post.published_at || post.created_at).format('MMMM D, YYYY')}</span>
                    </div>
                </header>

                <RichTextRenderer
                    className="rich-text-content"
                    content={post.content}
                />
            </article>
        </DefaultTemplate>
    );
};
